import { isDate, isValid } from 'date-fns'
import isUrl from 'is-url-superb'
import { type MouseEventHandler, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Conditional,
  Input,
  Timestamp,
  Tooltip,
  kuiperClassNames,
} from '@opallabs/kuiper'
import PreviewPane from './PreviewPane'
import * as styles from './styles'
import './styles.scss'

type UrlPreviewProps = {
  error?: boolean
  hasPreview?: boolean
  isEditing?: boolean
  layout?: string
  loading?: boolean
  onSubmitUrl: MouseEventHandler
  onUrlChange: (value: string) => void
  theme?: 'light' | 'dark'
  timestamp?: Date | string
  timezone: string
  url: string | undefined
}

export default function UrlPreview({
  error,
  hasPreview = false,
  isEditing = true,
  layout,
  loading = false,
  onSubmitUrl,
  onUrlChange,
  theme = 'light',
  timestamp = '',
  timezone,
  url,
}: UrlPreviewProps) {
  const [valid, setValid] = useState(
    typeof url === 'string' ? isUrl(url) : false
  )

  useEffect(() => {
    if (!url) {
      setValid(false)
    }
  }, [url])

  const onInputChange = (value: string) => {
    setValid(isUrl(value))
    onUrlChange(value)
  }

  const triggerOnKeyPress = {
    keyCodes: [13],
    triggerFunction: onSubmitUrl,
  }

  const validatedTimestamp = useMemo(() => {
    if (isDate(timestamp) && isValid(timestamp)) {
      return timestamp as Date
    }
    if (timestamp) {
      const maybeValidated = new Date(timestamp)
      if (isValid(maybeValidated)) {
        return maybeValidated
      }
    }
    return null
  }, [timestamp])

  return (
    <div className={kuiperClassNames('url-preview')}>
      <div className={kuiperClassNames('url-input')}>
        <div className={kuiperClassNames('url-input-field')}>
          <Input
            disabled={!isEditing}
            theme="white"
            placeholder="https://"
            onChange={onInputChange}
            triggerOnKeyPress={valid ? triggerOnKeyPress : false}
            value={url}
          >
            <Conditional condition={validatedTimestamp !== null && hasPreview}>
              <div
                className={kuiperClassNames('timestamp-wrapper')}
                css={styles.timeContainer({ theme })}
              >
                <Timestamp
                  format="MMM d · p"
                  styleOverrides={styles.timestamp}
                  theme={theme}
                  timestamp={validatedTimestamp ?? undefined}
                />
                <span
                  className={kuiperClassNames('timezone')}
                  css={styles.timezone}
                >
                  {timezone}
                </span>
              </div>
            </Conditional>
          </Input>
        </div>
        <UrlPreviewButton
          disabled={!valid || !isEditing || loading}
          icon={hasPreview || error ? 'refresh' : 'enter'}
          isEditing={isEditing}
          label={hasPreview || error ? 'Refresh' : 'Load'}
          onClick={onSubmitUrl}
          valid={valid}
        />
      </div>
      <PreviewPane
        error={error}
        onRefresh={onSubmitUrl}
        layout={layout}
        loading={loading}
      />
    </div>
  )
}

type UrlPreviewButtonProps = {
  disabled: boolean
  icon: string
  isEditing: boolean
  label: string
  onClick: UrlPreviewProps['onSubmitUrl']
  valid: boolean
}

function UrlPreviewButton({
  disabled,
  icon,
  isEditing,
  label,
  onClick,
  valid,
}: UrlPreviewButtonProps) {
  const button = (
    <Button
      disabled={disabled}
      icon={icon}
      label={label}
      onClick={onClick}
      position="right"
      type="secondary"
    />
  )

  if (!valid && isEditing) {
    return (
      <Tooltip
        position="top"
        className="url-preview-button"
        content="URL must contain an http:// or https:// protocol and domain"
        trigger={button}
      />
    )
  }

  return button
}
